/**
 * The answer key of a tents puzzle is the set of tents. Kept apart from the form component so
 * the eagerly loaded module file never pulls the author's board into the player bundle.
 */
import { blankRows, resizeRows } from "./progress";
import { EMPTY, TENT, type TentsDefinition, type TentsSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately empty campsite. `tentsOf` pads it to a blank grid.
 */
export function emptySolution(_definition: TentsDefinition): TentsSolution {
  return { rows: [] };
}

/** Whether a stored value is a tent grid at all, whatever its size. */
export function isTentGrid(value: unknown): value is TentsSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[A.]*$/.test(row));
}

/** Nothing queued: not a tent grid, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isTentGrid(value) || value.rows.length === 0;
}

/** Reads whatever is stored, however wrong, as a tent grid of the definition's size. */
export function tentsOf(value: unknown, definition: TentsDefinition): string[] {
  if (!isTentGrid(value)) return blankRows(definition.rows, definition.cols);
  return resizeRows(
    value.rows.map((row) => Array.from(row, (c) => (c === TENT ? TENT : EMPTY)).join("")),
    definition.rows,
    definition.cols,
  );
}
